import { cva, type VariantProps } from "class-variance-authority"
import type { ComponentProps } from "react"
import { cn } from "@/lib/utils.js"

/**
 * A badge is for the rare, load-bearing state — a veto, an active override, a
 * source that is down. Routine state in tables is a mark beside a word
 * (`Status.tsx`); a screen full of pills says nothing.
 *
 * Colour never travels alone: every badge takes a glyph as well as a word, so
 * it survives being read in greyscale — for colour-blind readers, and because
 * these screens get screenshotted into tickets.
 */
const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset",
  {
    variants: {
      tone: {
        allow: "bg-allow/10 text-allow ring-allow/25",
        prepay: "bg-prepay/10 text-prepay ring-prepay/25",
        review: "bg-review/10 text-review ring-review/25",
        critical: "bg-critical/10 text-critical ring-critical/25",
        neutral: "bg-muted text-muted-foreground ring-border",
        brand: "bg-accent text-accent-foreground ring-primary/25",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
)

export function Badge({
  className,
  tone,
  glyph,
  children,
  ...props
}: ComponentProps<"span"> & VariantProps<typeof badgeVariants> & { glyph?: string }) {
  return (
    <span className={cn(badgeVariants({ tone }), className)} {...props}>
      {glyph !== undefined && <span aria-hidden="true">{glyph}</span>}
      {children}
    </span>
  )
}
